import { createFileRoute, Navigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/auth/AuthProvider";
import { supabase } from "@/integrations/supabase/client";
import { SupportChatThread } from "@/components/support/SupportChatThread";
import { Loader2, LifeBuoy, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

export const Route = createFileRoute("/_app/support")({
  component: SupportPage,
});

function SupportPage() {
  const { t } = useTranslation();
  const { user, company, hasAnyRole, loading } = useAuth();
  const allowed = hasAnyRole(["admin", "owner"]);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [initializing, setInitializing] = useState(true);
  const [closing, setClosing] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (!user || !company || !allowed) return;
    let cancelled = false;
    (async () => {
      setInitializing(true);
      const { data: existing, error: selErr } = await supabase
        .from("support_conversations")
        .select("id")
        .eq("company_id", company.id)
        .maybeSingle();
      if (cancelled) return;
      if (selErr) {
        toast.error(t("supportPage.loadError"), { description: selErr.message });
        setInitializing(false);
        return;
      }
      if (existing?.id) {
        setConversationId(existing.id);
        setInitializing(false);
        return;
      }
      const { data: created, error: insErr } = await supabase
        .from("support_conversations")
        .insert({ company_id: company.id, subject: t("supportPage.subjectDefault") })
        .select("id")
        .single();
      if (cancelled) return;
      if (insErr) {
        toast.error(t("supportPage.startError"), { description: insErr.message });
      } else if (created) {
        setConversationId(created.id);
      }
      setInitializing(false);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, company, allowed, reloadKey]);

  async function handleEndAndDelete() {
    if (!conversationId || !company) return;
    setClosing(true);
    try {
      const { data: msgs, error: msgsErr } = await supabase
        .from("support_messages")
        .select("attachment_path")
        .eq("conversation_id", conversationId)
        .not("attachment_path", "is", null);
      if (msgsErr) throw msgsErr;

      const paths = (msgs ?? [])
        .map((m) => m.attachment_path as string | null)
        .filter((p): p is string => !!p);

      if (paths.length > 0) {
        const { error: rmErr } = await supabase.storage.from("support-attachments").remove(paths);
        if (rmErr) {
          console.warn("Failed to remove attachments:", rmErr.message);
        }
      }

      const { error: delMsgErr } = await supabase
        .from("support_messages")
        .delete()
        .eq("conversation_id", conversationId);
      if (delMsgErr) throw delMsgErr;

      const { error: delConvErr } = await supabase
        .from("support_conversations")
        .delete()
        .eq("id", conversationId);
      if (delConvErr) throw delConvErr;

      toast.success(t("supportPage.endedToast"));
      setConversationId(null);
      setReloadKey((k) => k + 1);
    } catch (e) {
      const msg = e instanceof Error ? e.message : t("supportPage.endFallback");
      toast.error(t("supportPage.endError"), { description: msg });
    } finally {
      setClosing(false);
    }
  }

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (!allowed) return <Navigate to="/dashboard" />;

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-md bg-primary/10 text-primary">
            <LifeBuoy className="h-5 w-5" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">{t("supportPage.title")}</h1>
            <p className="text-sm text-muted-foreground">{t("supportPage.subtitle")}</p>
          </div>
        </div>

        {conversationId && !initializing && (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline" size="sm" className="gap-2" disabled={closing}>
                {closing ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Trash2 className="h-4 w-4" />
                )}
                {t("supportPage.endAndDelete")}
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>{t("supportPage.endTitle")}</AlertDialogTitle>
                <AlertDialogDescription>{t("supportPage.endDesc")}</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel disabled={closing}>{t("supportPage.cancel")}</AlertDialogCancel>
                <AlertDialogAction
                  onClick={(e) => {
                    e.preventDefault();
                    void handleEndAndDelete();
                  }}
                  disabled={closing}
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                >
                  {t("supportPage.endAndDelete")}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
      </div>

      {initializing || !conversationId || !company ? (
        <div className="flex h-64 items-center justify-center rounded-lg border border-border">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <SupportChatThread
          conversationId={conversationId}
          companyId={company.id}
          viewerRole="company"
        />
      )}
    </div>
  );
}
